import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * El color de una etiqueta se genera solo, girando el matiz.
 *
 * Se guarda como `h:<grados>` —`h:137`— y se pinta con
 * `hsl(matiz var(--tag-s) var(--tag-l))`. El matiz es libre; la saturación y
 * la luminosidad NO: las fija el tema, porque son las que deciden si el texto
 * se lee y dependen del fondo. Un hex libre elegido por quien crea la etiqueta
 * es exactamente cómo aparece un amarillo ilegible sobre blanco.
 *
 * El tinte se usa como TINTA —borde y texto sobre superficie neutra—, nunca
 * como relleno.
 */

/**
 * Ángulo áureo: 360 / φ².
 *
 * Girar el matiz esta cantidad en cada etiqueta nueva es lo que hace que dos
 * creadas seguidas salgan claramente distintas y que la serie entera se
 * reparta por la rueda sin agruparse. Con un paso fijo —60°, por ejemplo— la
 * séptima etiqueta repetiría el color de la primera; con éste, la vuelta nunca
 * cae encima de un matiz ya usado.
 */
const ANGULO_AUREO = 137.508;

/**
 * El color de la etiqueta número `n` del vocabulario.
 *
 * Depende de la POSICIÓN y no del nombre: dos etiquetas con nombres parecidos
 * saldrían con matices vecinos, que es justo lo que no se quiere de un color
 * cuyo trabajo es distinguirlas de un vistazo.
 */
export function autoTagColor(n: number): string {
  return `h:${Math.round((n * ANGULO_AUREO) % 360)}`;
}

/**
 * Muestras para elegir a mano, repartidas por la rueda.
 *
 * Doce, con el mismo giro áureo, para que la fila de puntos del gestor no
 * tenga dos casi iguales.
 */
export const TAG_COLORS = Array.from({ length: 12 }, (_, i) => autoTagColor(i));

/**
 * Nombres de la primera versión (`slate`, `violet`, …).
 *
 * Se mantienen porque hay etiquetas guardadas con ellos: al cambiar a matices
 * generados, las que ya existían habrían pasado a pintarse todas del color por
 * defecto. No se crean nuevos.
 */
const HEREDADOS: Record<string, string> = {
  slate: 'h:220',
  violet: 'h:275',
  teal: 'h:175',
  amber: 'h:40',
  rose: 'h:350',
  lime: 'h:95',
};

/** El CSS del tinte, sea cual sea la forma en que esté guardado el color. */
export function tagTint(color: string | undefined): string {
  const valor = HEREDADOS[color ?? ''] ?? color ?? 'h:220';
  const grados = Number(valor.startsWith('h:') ? valor.slice(2) : NaN);
  // Un valor que no se entiende cae al neutro en vez de romper el estilo: una
  // etiqueta con un color raro debe verse sosa, no invisible.
  const matiz = Number.isFinite(grados) ? grados : 220;
  return `hsl(${matiz} var(--tag-s, 58%) var(--tag-l, 38%))`;
}

interface Props {
  name: string;
  color?: string;
  /** Con esto la píldora trae una x para quitarla. */
  onRemove?: () => void;
  /** Pulsable — para el filtro, donde la píldora es un interruptor. */
  onClick?: () => void;
  /** Marcada. Sólo tiene sentido junto con `onClick`. */
  active?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Una etiqueta, pintada.
 *
 * Sirve para las tres cosas que se hacen con una etiqueta —verla, quitarla y
 * marcarla en un filtro— porque las tres deben verse idénticas: si la píldora
 * del filtro no fuera exactamente la misma que la de la lista, nadie
 * relacionaría lo que marca con lo que aparece.
 */
export function TagPill({
  name,
  color,
  onRemove,
  onClick,
  active = false,
  size = 'md',
  className,
}: Props) {
  const tinte = tagTint(color);
  const contenido = (
    <>
      <span
        aria-hidden
        className="h-1.5 w-1.5 flex-none rounded-full"
        style={{ background: tinte }}
      />
      <span className="truncate">{name}</span>
    </>
  );

  const base = cn(
    'inline-flex max-w-[14rem] items-center gap-1.5 rounded-full border font-semibold transition-colors',
    size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]',
    className,
  );

  // Marcada = el tinte pasa al borde y al texto. Sin relleno saturado: es lo
  // que mantiene el contraste al cambiar de tema.
  const estilo = active
    ? { borderColor: tinte, color: tinte }
    : { borderColor: 'var(--color-border-subtle)', color: 'var(--color-fg-muted)' };

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(base, 'cursor-pointer hover:border-current')}
        style={{ ...estilo, background: 'var(--color-bg-surface)' }}
      >
        {contenido}
      </button>
    );
  }

  return (
    <span
      className={base}
      style={{ ...estilo, background: 'var(--color-bg-surface)' }}
    >
      {contenido}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          // El nombre va en la etiqueta accesible: «quitar» a secas no dice
          // cuál, y en una fila con seis píldoras eso no sirve de nada.
          aria-label={`${name} ×`}
          className="-mr-0.5 grid h-3.5 w-3.5 flex-none place-items-center rounded-full opacity-60 hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
}
